import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    BadResponseWithError,
    TournamentsWithPagination,
} from '@shared/models/common.interface';
import { NotificationsService } from '@shared/services/notifications.service';
import { catchError, map, of, switchMap } from 'rxjs';
import {
    PlayerProfilesWithPagination,
    ProfileInfo,
} from '../../models/api/response/user-management.response';
import { UserManagementService } from '../../services/user-management.service';
import { UserManagementActions } from '../actions/actions-type.action';

@Injectable({ providedIn: 'root' })
export class UserManagementEffect {
    unbanUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.unbanUser),
            switchMap(({ profileId }) =>
                this.userManagementService.unbanUser(profileId).pipe(
                    map(() => {
                        this.notificationService.showSuccessMessage(
                            'User has been unbanned'
                        );

                        return UserManagementActions.unbanUserSuccess();
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationService.showError(
                            response.error.message
                        );
                        return of(
                            UserManagementActions.unbanUserFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    sendBroadcastNotification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.sendBroadcastNotification),
            switchMap(({ payload }) =>
                this.userManagementService
                    .sendBroadcastNotification(payload.text)
                    .pipe(
                        map(() => {
                            this.notificationService.showSuccessMessage(
                                'Notification Successfully Sent'
                            );

                            return UserManagementActions.sendBroadcastNotificationSuccess();
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationService.showError(
                                response.error.message
                            );

                            return of(
                                UserManagementActions.sendBroadcastNotificationFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    fetchUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.fetchUsers),
            switchMap(({ payload }) =>
                this.userManagementService.fetchPlayers(payload).pipe(
                    map((response: PlayerProfilesWithPagination) =>
                        UserManagementActions.fetchUsersSuccess({
                            payload: response,
                        })
                    ),
                    catchError((response: BadResponseWithError) => {
                        this.notificationService.showError(
                            response.error.message
                        );

                        return of(
                            UserManagementActions.fetchUsersFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    fetchProfileInfo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.fetchProfileInfo),
            switchMap(({ payload }) =>
                this.userManagementService
                    .fetchProfileInfo(payload.profileId)
                    .pipe(
                        map((response: ProfileInfo) =>
                            UserManagementActions.fetchProfileInfoSuccess({
                                payload: response,
                            })
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationService.showError(
                                response.error.message
                            );

                            return of(
                                UserManagementActions.fetchProfileInfoFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );
    fetchPlayersIpLogs$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.fetchUserIpLogs),
            switchMap(({ payload }) =>
                this.userManagementService
                    .fetchPlayerIpLogs(payload.profileId)
                    .pipe(
                        map((response: any) =>
                            UserManagementActions.fetchUserIpLogsSuccess({
                                payload: response,
                            })
                        ),
                        catchError((response: BadResponseWithError) => {
                            return of(
                                UserManagementActions.fetchUserIpLogsFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );

    fetchProfileTournaments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.fetchProfileTournaments),
            switchMap(({ payload }) =>
                this.userManagementService
                    .fetchProfileTournaments(payload.profileId)
                    .pipe(
                        map((response: TournamentsWithPagination) =>
                            UserManagementActions.fetchProfileTournamentsSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationService.showError(
                                response.error.message
                            );

                            return of(
                                UserManagementActions.profileTurnOff2faFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );

    turnOff2fa$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.profileTurnOff2fa),
            switchMap(({ payload }) =>
                this.userManagementService
                    .profileTurnOff2fa(payload.userId)
                    .pipe(
                        map(() => {
                            this.notificationService.showSuccessMessage(
                                'Two-factor authentication has been turned off.'
                            );

                            return UserManagementActions.profileTurnOff2faSuccess();
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationService.showError(
                                response.error.message
                            );

                            return of(
                                UserManagementActions.profileTurnOff2faFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );

    resetProfilePassword$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.resetProfilePassword),
            switchMap(({ payload }) =>
                this.userManagementService
                    .resetProfilePassword(payload.userId)
                    .pipe(
                        map(() => {
                            this.notificationService.showSuccessMessage(
                                'The password has been reset.'
                            );

                            return UserManagementActions.resetProfilePasswordSuccess();
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationService.showError(
                                response.error.message
                            );

                            return of(
                                UserManagementActions.resetProfilePasswordFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    createProfile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.createProfile),
            switchMap(({ payload }) =>
                this.userManagementService.createProfile(payload).pipe(
                    map(() => {
                        this.notificationService.showSuccessMessage(
                            'User has been created successfully.'
                        );

                        return UserManagementActions.createProfileSuccess();
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationService.showError(
                            response.error.message
                        );

                        return of(
                            UserManagementActions.createProfileFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    updateProfile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.updateProfile),
            switchMap(({ payload }) =>
                this.userManagementService
                    .updateProfile(payload.profileId, payload.request)
                    .pipe(
                        map(() => {
                            this.notificationService.showSuccessMessage(
                                'User information has been updated.'
                            );

                            return UserManagementActions.updateProfileSuccess();
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationService.showError(
                                response.error.message
                            );

                            return of(
                                UserManagementActions.updateProfileFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );

    deleteProfile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.deleteProfile),
            switchMap(({ payload }) =>
                this.userManagementService
                    .deleteProfile(payload.profileId)
                    .pipe(
                        map(() => {
                            this.notificationService.showSuccessMessage(
                                'User information has been deleted.'
                            );

                            return UserManagementActions.deleteProfileSuccess();
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationService.showError(
                                response.error.message
                            );

                            return of(
                                UserManagementActions.deleteProfileFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );

    depositAccount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.depositProfileWallet),
            switchMap(({ payload }) => {
                return this.userManagementService
                    .depositAccount({
                        profileId: payload.playerId,
                        amount: payload.amount,
                        currency: payload.amountCurrency,
                        description: payload.paymentDescription,
                    })
                    .pipe(
                        map((response: any) => {
                            this.notificationService.showSuccessMessage(
                                'User information has been updated.'
                            );
                            return UserManagementActions.depositProfileWalletSuccess();
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationService.showError(
                                response.error.message
                            );

                            return of(
                                UserManagementActions.depositProfileWalletFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );

    constructor(
        private actions$: Actions,
        private userManagementService: UserManagementService,
        private notificationService: NotificationsService
    ) {}
}
